import { ARTIFACT_IDS } from '@/artifacts/navigation-map';
import type { ArtifactId } from '@/artifacts';

/**
 * Top-level route path constants. Only entries actually consumed by
 * `createBrowserRouter` (`src/routes/index.tsx`) and route guards are
 * listed here. Workspace-scoped paths under `/map/*` are built via
 * `mapRoutes` below — they are registry-driven, so adding a new
 * artifact does not require touching this file.
 */
export const ROUTES = {
  CALLBACK: '/auth/callback',
  ONBOARDING: '/onboarding',
  SETTINGS: '/settings',
  INVITATION: '/invite/:token',
  WORKSPACE: '/w/:shortId',
  WORKSPACE_MAP_CONFIG: '/w/:shortId/map-config',
  /** Public, shell-less read-only shared map. Optional segments select an artifact detail. */
  SHARE: '/share/:token/:singular?/:itemId?',
  /** Public, shell-less embeddable shared map (iframe). Optional segments select an artifact detail. */
  EMBED: '/embed/:token/:singular?/:itemId?',
} as const;

/** Build helpers for the public share / embed routes. */
export const shareRoutes = {
  share: (token: string): string => `/share/${token}`,
  embed: (token: string): string => `/embed/${token}`,
  /** URL-driven artifact detail on the public share page. */
  detail: (token: string, singular: string, itemId: string): string =>
    `/share/${token}/${singular}/${itemId}`,
  /** URL-driven artifact detail on the embed page. */
  embedDetail: (token: string, singular: string, itemId: string): string =>
    `/embed/${token}/${singular}/${itemId}`,
} as const;

/**
 * The `/w/:shortId/map-config` section segment. A first-run/edit gate route, not
 * a workspace section like `dashboard`/`map` — consumers (e.g. the workspace
 * switcher) use it to recognize the gate route without re-hardcoding the string.
 */
export const MAP_CONFIG_SECTION = 'map-config';

/**
 * Build helpers for workspace-scoped map routes.
 *
 * Every helper takes a `shortId` plus the registry-derived segment
 * (`ArtifactId` for background, `singular` for overlay URLs) so adding a
 * new artifact only requires registering it in `@/artifacts` — no new
 * entries here.
 */
export const mapRoutes = {
  root: (shortId: string): string => `/w/${shortId}/map`,
  mapConfig: (shortId: string): string => `/w/${shortId}/${MAP_CONFIG_SECTION}`,
  background: (artifactId: ArtifactId, shortId: string): string =>
    `/w/${shortId}/map/${artifactId}`,
  detail: (singular: string, shortId: string, itemId: string): string =>
    `/w/${shortId}/map/${singular}/${itemId}`,
  edit: (singular: string, shortId: string, itemId: string): string =>
    `/w/${shortId}/map/${singular}/${itemId}/edit`,
  new: (singular: string, shortId: string): string =>
    `/w/${shortId}/map/${singular}/new`,
} as const;

// URL section parsing for `/w/:shortId/:section/...` paths.
// Index 3 corresponds to `:section` after splitting the pathname on `/`.
export const SECTION_SEGMENT_INDEX = 3;
export const DEFAULT_SECTION = 'dashboard';

// Sections that render against the map background (the bare /map view plus
// every artifact route nested under it). Built from ARTIFACT_IDS so adding a
// new artifact does not require touching this list.
export const MAP_SECTIONS = ['map', ...ARTIFACT_IDS] as const;
export type MapSection = (typeof MAP_SECTIONS)[number];
